import { Check, X } from "lucide-react";
import Button from "../components/Button";

const features = [
  "Crypto escrow on every payment",
  "USDT & USDC support",
  "Multi-chain: Solana, Ethereum, Polygon, Base",
  "Phone-verified users",
  "Anonymous profiles until payment",
  "Anti-bypass encrypted chat",
  "Real-time secure messaging",
  "Custom KYC & skill verification",
  "Job posting & matching system",
  "Contract & milestone management",
  "Internal reputation system",
  "Dispute resolution",
  "Admin panel & audit logs",
  "Device fingerprinting & ban system",
  "Worker & agency dashboards",
];

const comparisons = [
  {
    label: "Crypto payments",
    hirefire: true,
    traditional: "Bank transfers",
  },
  {
    label: "Anonymous profiles",
    hirefire: true,
    traditional: "Full identity required",
  },
  {
    label: "Escrow protection",
    hirefire: true,
    traditional: "Trust-based",
  },
  {
    label: "10% flat fee",
    hirefire: true,
    traditional: "20-30% + hidden fees",
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="section-padding relative">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-glow opacity-20 pointer-events-none" />

      <div className="section-container relative">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-sm font-medium text-accent-purple uppercase tracking-widest mb-4 block font-mono">
            Pricing
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            Simple,{" "}
            <span className="text-gradient">transparent</span> fees
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            No plans. No tiers. No subscriptions. One flat fee on every
            completed payment. That's it.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-10 items-start">
          {/* Left: Pricing card with animated gradient border */}
          <div className="w-full lg:w-1/2 space-y-6">
            {/* For Agencies — 10% */}
            <div className="gradient-border animate-border-glow rounded-2xl">
              <div className="rounded-2xl p-8 bg-surface">
                <span className="text-accent-blue uppercase tracking-widest text-xs font-mono mb-3 block">For Agencies</span>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-display text-6xl sm:text-7xl font-bold text-white">10%</span>
                  <span className="text-gray-400 text-lg">per payment</span>
                </div>
                <p className="text-sm text-gray-500 mb-6">
                  Deducted automatically when escrow is released to the worker.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-8">
                  {features.map((feature) => (
                    <div key={feature} className="flex items-start gap-2.5">
                      <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>
                <Button variant="gradient" size="lg" href="#cta" className="w-full sm:w-auto">
                  Start Hiring
                </Button>
              </div>
            </div>

            {/* For Workers — Free */}
            <div className="rounded-2xl border border-surface-border p-8 bg-surface/50">
              <span className="text-accent-purple uppercase tracking-widest text-xs font-mono mb-3 block">For Workers</span>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-display text-6xl sm:text-7xl font-bold text-gradient">Free</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                After phone verification. Apply to jobs, chat with agencies, and get paid in crypto — no platform fees for workers.
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {["Apply to unlimited jobs", "Crypto wallet included", "Anonymous profile", "Escrow-protected payments"].map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <Check size={14} className="text-green-500 flex-shrink-0" />
                    <span className="text-sm text-gray-300">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Why HirenFire comparison table */}
          <div className="w-full lg:w-1/2 lg:pt-4">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white mb-2">
              Why HirenFire?
            </h3>
            <p className="text-gray-400 text-sm mb-8">
              See how we compare to traditional hiring platforms.
            </p>

            {/* Comparison table */}
            <div className="rounded-xl border border-surface-border overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-3 bg-surface/80 border-b border-surface-border px-5 py-3">
                <span className="text-xs font-mono uppercase tracking-wider text-gray-500">
                  Feature
                </span>
                <span className="text-xs font-mono uppercase tracking-wider text-accent-blue text-center">
                  HirenFire
                </span>
                <span className="text-xs font-mono uppercase tracking-wider text-gray-500 text-center">
                  Traditional
                </span>
              </div>

              {/* Rows */}
              {comparisons.map((row, i) => (
                <div
                  key={row.label}
                  className={`grid grid-cols-3 px-5 py-4 items-center ${
                    i < comparisons.length - 1
                      ? "border-b border-surface-border/50"
                      : ""
                  } hover:bg-white/[0.02] transition-colors duration-200`}
                >
                  <span className="text-sm text-white font-medium">
                    {row.label}
                  </span>
                  <div className="flex justify-center">
                    <div className="flex items-center gap-1.5 text-green-400">
                      <Check size={16} />
                      <span className="text-xs hidden sm:inline">Yes</span>
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <div className="flex items-center gap-1.5 text-red-400/70">
                      <X size={16} />
                      <span className="text-xs text-gray-500 hidden sm:inline">
                        {row.traditional}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary callout */}
            <div className="mt-6 p-4 rounded-lg bg-accent-blue/5 border border-accent-blue/10">
              <p className="text-sm text-gray-300">
                <span className="text-accent-blue font-semibold">Bottom line:</span>{" "}
                Lower fees, stronger privacy, and guaranteed payments through
                crypto escrow. No compromises.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom note */}
        <div className="text-center mt-14">
          <p className="text-sm text-gray-500">
            No hidden fees. No lock-in. Workers receive the full contract
            amount minus the 10% platform fee.
          </p>
        </div>
      </div>
    </section>
  );
}
